import React, { useState, useContext, useEffect, useMemo } from "react";
import {
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
} from "@mui/material";
import AuthContext from "../../context/AuthContext/AuthContext";
import { useCustomTranslation } from "../../hooks/useCustomTranslation";
import {
  useApiClientQuery,
  useApiClientMutation,
} from "../../hooks/useApiClient";
import api_endpoints from "../../api";
import { NumericFormat } from "react-number-format";
import { formatDateTime } from "../../utils";
import { useSuccessAlert } from "../../context/SuccessAlertContext/SuccessAlertContext";
import {
  PAYMENT_TYPE,
  PROMISE_TYPE,
  PAYMENT_FREQUENCY,
} from "../../enums/PromiseEnums";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

const CreatePromise = ({ onClose, defaultCampaign }) => {
  const { t } = useCustomTranslation("Create Promise");
  const [emailError, setEmailError] = useState(false);
  const [acceptCash, setAcceptCash] = useState(true);
  const [dueDateError, setDueDateError] = useState(false);
  const authContext = useContext(AuthContext);
  const {
    showSuccessAlert,
    setShowSuccessAlert,
    messageSuccessAlert,
    setMessageSuccessAlert,
  } = useSuccessAlert();

  const [formData, setFormData] = useState({
    donor_id: "",
    campaign_id: "",
    first_name: "",
    last_name: "",
    phone: "",
    address: "",
    email: "",
    zip_code: "",
    payment_type: PAYMENT_TYPE.CASH,
    promise_type: PROMISE_TYPE.ONE_TIME_PAYMENT,
    promise_amount: "",
    payment_frequency: "",
    due_date: "",
    end_date: "",
  });

  const campaignsQuery = useApiClientQuery(
    () =>
      api_endpoints.getCampaigns(
        authContext.authContext.accessToken.organization_id,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        true // is_fundraiser = true
      ),
    {
        queryKey: [
          "campaigns_data",
          authContext.authContext.accessToken.organization_id,
        ],
    }
  );

  const donorsQuery = useApiClientQuery(
    () =>
      api_endpoints.getDonors(
        null,
        null,
        authContext.authContext.accessToken.organization_id
      ),
    {
      queryKey: [
        "donors_data",
        authContext.authContext.accessToken.organization_id,
      ],
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevValue) => ({
      ...prevValue,
      [name]: value,
    }));

    if (name === "email") {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      setEmailError(!emailPattern.test(value));
    }

    if (name === "payment_type") {
      setFormData((prevValue) => ({
        ...prevValue,
        payment_frequency: null, // keep key consistent with state
        due_date: null,
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (emailError) {
      console.error("Invalid email address");
      return;
    }

    if (
      formData.promise_type === PROMISE_TYPE.ONE_TIME_PAYMENT &&
      !formData.due_date
    ) {
      setDueDateError(true);
      return;
    }

    const promiseData = {
      donor_id: formData.donor_id,
      campaign_id: formData.campaign_id,
      first_name: formData.first_name,
      last_name: formData.last_name,
      phone: formData.phone,
      address: formData.address,
      email: formData.email,
      zip_code: formData.zip_code,
      promise_amount: formData.promise_amount.replace(/[^0-9.]/g, "") ?? 0,
      given_amount: 0,
      due_date: formData.due_date ? formatDateTime(formData.due_date) : null,
      end_date: formData.end_date ? formatDateTime(formData.end_date) : null,
      promise_type: formData.promise_type,
      payment_type: formData.payment_type,
      payment_frequency:
        formData.promise_type === PROMISE_TYPE.SPLIT_PAYMENT
          ? (formData.payment_frequency || null)
          : null,
    };

    createPromiseMutation.mutate(promiseData);
  };

  const createPromiseMutation = useApiClientMutation(
    api_endpoints.createPromise,
    [`view_promise`],
    (data) => {
      setMessageSuccessAlert(data.message);
      setShowSuccessAlert(true);
      onClose();
    }
  );

  const handleDonorChange = (_, value) => {
    setFormData((prev) => ({
      ...prev,
      donor_id: value ? value._id : "",
    }));
  };

  const handleCampaignChange = (_, value) => {
    setFormData((prev) => ({
      ...prev,
      campaign_id: value ? value._id : "",
    }));
    if (value && value.can_donate_with_cash) {
      setAcceptCash(true);
    } else {
      setAcceptCash(false);
      setFormData((prev) => ({
        ...prev,
        payment_type: PAYMENT_TYPE.CREDIT_CARD,
      }));
    }
  };

  // ----- Default campaign wiring -----

  const campaigns = campaignsQuery.data ?? [];

  // Find a matching campaign from incoming defaultCampaign ({ id, name })
  const defaultCampaignOption = useMemo(() => {
    if (!defaultCampaign) return null;
    return (
      campaigns.find(
        (c) => c?._id === defaultCampaign.id || c?.name === defaultCampaign.name
      ) || null
    );
  }, [campaigns, defaultCampaign]);

  // Seed the form with the default when data is ready (and not already set)
  useEffect(() => {
    if (defaultCampaignOption && !formData.campaign_id) {
      setFormData((prev) => ({ ...prev, campaign_id: defaultCampaignOption._id }));
      setAcceptCash(!!defaultCampaignOption.can_donate_with_cash);
      // If cash not allowed, force payment type to credit card
      if (!defaultCampaignOption.can_donate_with_cash) {
        setFormData((prev) => ({ ...prev, payment_type: PAYMENT_TYPE.CREDIT_CARD }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCampaignOption, formData.campaign_id]);

  // Controlled value for the Campaign Autocomplete
  const selectedCampaignObj = useMemo(() => {
    if (formData.campaign_id) {
      return campaigns.find((c) => c._id === formData.campaign_id) || null;
    }
    return defaultCampaignOption || null;
  }, [campaigns, formData.campaign_id, defaultCampaignOption]);

  return (
    <form onSubmit={handleSubmit}>
      <div className="grid grid-cols-2 gap-4 my-4">
        <Autocomplete
          options={donorsQuery.data?.filter(
            (donor) => donor && donor.email && donor.email.trim() !== ""
          ) || []}
          name="donor"
          onChange={handleDonorChange}
          getOptionLabel={(option) => option.email}
          isOptionEqualToValue={(o, v) => o._id === v._id}
          renderInput={(params) => <TextField {...params} label={t("customer", "Customer")} />}
        />

        <Autocomplete
          options={
            campaignsQuery.data?.filter(
              (campaign) => campaign && campaign.name && campaign.name.trim() !== ""
            ) || []
          }
          name="campaign_id"
          value={selectedCampaignObj}
          onChange={handleCampaignChange}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(o, v) => o._id === v._id}
          renderInput={(params) => (
            <TextField {...params} label="Campaign" required />
          )}
        />
      </div>

      {!formData.donor_id && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <TextField
              fullWidth
              label={t("first_name")}
              required
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
            />
            <TextField
              fullWidth
              label={t("last_name")}
              required
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
            />
          </div>
          <div className="my-4">
            <TextField
              fullWidth
              label={t("phone")}
              required
              name="phone"
              value={formData.phone}
              onChange={handleChange}
            />
          </div>
          <div className="my-4">
            <TextField
              fullWidth
              label={t("address")}
              required
              name="address"
              value={formData.address}
              onChange={handleChange}
            />
          </div>
          <div className="grid grid-cols-2 gap-4 my-4">
            <TextField
              label={t("email")}
              required
              error={emailError}
              helperText={emailError ? "Invalid email address" : ""}
              name="email"
              value={formData.email}
              onChange={handleChange}
            />
            <TextField
              label={t("zip_code")}
              required
              name="zip_code"
              value={formData.zip_code}
              onChange={handleChange}
            />
          </div>
        </>
      )}

      <div className="grid grid-cols-1 gap-4 my-4">
        <FormControl fullWidth required>
          <InputLabel>{t("payment_type")}</InputLabel>
          <Select
            name="payment_type"
            value={formData.payment_type}
            onChange={handleChange}
            label={t("payment_type")}
          >
            <MenuItem value={PAYMENT_TYPE.CREDIT_CARD}>
              {t("credit_card")}
            </MenuItem>
            <MenuItem value={PAYMENT_TYPE.CHEQUE}>{t("cheque")}</MenuItem>
            <MenuItem value={PAYMENT_TYPE.BANK_TRANSFER}>
              {t("bank_transfer")}
            </MenuItem>
            {acceptCash && (
              <MenuItem value={PAYMENT_TYPE.CASH}>{t("cash")}</MenuItem>
            )}
          </Select>
        </FormControl>
      </div>

      <div className="grid grid-cols-2 gap-4 my-4">
        <FormControl fullWidth required>
          <InputLabel>{t("promise_type")}</InputLabel>
          <Select
            name="promise_type"
            value={formData.promise_type}
            onChange={handleChange}
            label={t("promise_type")}
          >
            <MenuItem value={PROMISE_TYPE.ONE_TIME_PAYMENT}>
              {t("one_time_payment")}
            </MenuItem>
            <MenuItem value={PROMISE_TYPE.SPLIT_PAYMENT}>
              {t("split_payment")}
            </MenuItem>
            {/* <MenuItem value={PROMISE_TYPE.RECURRING}>
              {t("recurring")}
            </MenuItem> */}
          </Select>
        </FormControl>

        {formData.promise_type === PROMISE_TYPE.ONE_TIME_PAYMENT && (
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr-ca">
            <DatePicker
              value={formData.due_date}
              onChange={(newValue) => {
                setFormData((prevValue) => ({
                  ...prevValue,
                  due_date: newValue,
                }));
                setDueDateError(false);
              }}
              label={t("due_date")}
              sx={{ width: "100%" }}
              minDate={dayjs(new Date())}
              slotProps={{
                textField: {
                  error: dueDateError,
                  required: true,
                },
              }}
            />
          </LocalizationProvider>
        )}

        {formData.promise_type === PROMISE_TYPE.SPLIT_PAYMENT && (
          <FormControl fullWidth>
            <InputLabel>{t("frequency")}</InputLabel>
            <Select
              name="payment_frequency"
              value={formData.payment_frequency}
              onChange={handleChange}
              label={t("frequency")}
            >
              <MenuItem value="">{t("none")}</MenuItem>
              <MenuItem value={PAYMENT_FREQUENCY.DAILY}>{t("daily")}</MenuItem>
              <MenuItem value={PAYMENT_FREQUENCY.WEEKLY}>{t("weekly")}</MenuItem>
              <MenuItem value={PAYMENT_FREQUENCY.EVERY_2_WEEKS}>
                {t("every_2_weeks")}
              </MenuItem>
              <MenuItem value={PAYMENT_FREQUENCY.MONTHLY}>{t("monthly")}</MenuItem>
              <MenuItem value={PAYMENT_FREQUENCY.EVERY_2_MONTHS}>
                {t("every_2_months")}
              </MenuItem>
              <MenuItem value={PAYMENT_FREQUENCY.QUARTERLY}>
                {t("quarterly")}
              </MenuItem>
              <MenuItem value={PAYMENT_FREQUENCY.EVERY_4_MONTHS}>
                {t("every_4_months")}
              </MenuItem>
              <MenuItem value={PAYMENT_FREQUENCY.BIMONTHLY}>
                {t("bimonthly")}
              </MenuItem>
              <MenuItem value={PAYMENT_FREQUENCY.SEMI_ANNUALLY}>
                {t("semi_annually")}
              </MenuItem>
              <MenuItem value={PAYMENT_FREQUENCY.ANNUALLY}>
                {t("annually")}
              </MenuItem>
            </Select>
          </FormControl>
        )}

        {formData.promise_type === PROMISE_TYPE.SPLIT_PAYMENT && (
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr-ca">
            <DatePicker
              value={formData.end_date || null}
              onChange={(newValue) => {
                setFormData((prevValue) => ({
                  ...prevValue,
                  end_date: newValue,
                }));
              }}
              label={t("end_date")}
              sx={{ width: "100%" }}
              minDate={formData.due_date ? dayjs(formData.due_date) : dayjs(new Date())}
              slotProps={{
                textField: {
                  helperText: t("end_date_helper", "Optional: when the promise ends"),
                },
              }}
            />
          </LocalizationProvider>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 my-4" style={{ display: 'none' }}>
        {formData.promise_type === PROMISE_TYPE.RECURRING && (
          <FormControl fullWidth required>
            <InputLabel>{t("frequency")}</InputLabel>
            <Select
              name="payment_frequency"
              value={formData.payment_frequency}
              onChange={handleChange}
              label={t("frequency")}
            >
              <MenuItem value={PAYMENT_FREQUENCY.DAILY}>{t("daily")}</MenuItem>
              <MenuItem value={PAYMENT_FREQUENCY.WEEKLY}>{t("weekly")}</MenuItem>
              <MenuItem value={PAYMENT_FREQUENCY.EVERY_2_WEEKS}>
                {t("every_2_weeks")}
              </MenuItem>
              <MenuItem value={PAYMENT_FREQUENCY.MONTHLY}>{t("monthly")}</MenuItem>
              <MenuItem value={PAYMENT_FREQUENCY.EVERY_2_MONTHS}>
                {t("every_2_months")}
              </MenuItem>
              <MenuItem value={PAYMENT_FREQUENCY.QUARTERLY}>
                {t("quarterly")}
              </MenuItem>
              <MenuItem value={PAYMENT_FREQUENCY.EVERY_4_MONTHS}>
                {t("every_4_months")}
              </MenuItem>
              <MenuItem value={PAYMENT_FREQUENCY.BIMONTHLY}>
                {t("bimonthly")}
              </MenuItem>
              <MenuItem value={PAYMENT_FREQUENCY.SEMI_ANNUALLY}>
                {t("semi_annually")}
              </MenuItem>
              <MenuItem value={PAYMENT_FREQUENCY.ANNUALLY}>
                {t("annually")}
              </MenuItem>
            </Select>
          </FormControl>
        )}
      </div>

      <div>
        <NumericFormat
          fullWidth
          value={formData.promise_amount}
          onChange={handleChange}
          customInput={TextField}
          thousandSeparator
          valueIsNumericString
          decimalScale={2}
          required
          name="promise_amount"
          prefix="$ "
          label={t("promise_amount")}
        />
      </div>

      <div className="flex justify-end mt-4 space-x-4">
        <Button variant="outlined" color="error" onClick={onClose}>
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          type="submit"
          loading={createPromiseMutation.isPending}
        >
          {t("save")}
        </Button>
      </div>
    </form>
  );
};

export default CreatePromise;
